// Reducer + helpers for the multi-step login flow. No React imports — pure TS.
// Port of frontend/src/app/login/_providers/login-flow-helpers.ts (v2 edition).
import type {
  LoginFlowState,
  LoginFlowAction,
  IdentifyResult,
  LoginStepKind,
} from "../types";

export const SUBTITLES: Record<LoginStepKind, string> = {
  email: "Portal de Acesso",
  need_payment: "Acesso Necessário",
  need_matrix: "Criar Conta Matrix",
  need_2fa: "Verificação em Duas Etapas",
  login: "Bem-vindo de Volta",
};

const CHECKOUT_FALLBACK = "/comprar";
const MATRIX_SIGNUP_URL = "https://stke.me/l/1425/1616";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(email: string): boolean {
  const t = email.trim();
  if (t.length < 3 || t.length > 254) return false;
  return EMAIL_RE.test(t);
}

export function isSafeUrl(url: string): boolean {
  if (url.startsWith("/") && !url.startsWith("//")) return true;
  try {
    const p = new URL(url);
    return p.protocol === "https:" || p.protocol === "http:";
  } catch {
    return false;
  }
}

export function sanitizeUrl(url: string): string {
  return isSafeUrl(url) ? url : "";
}

export function createInitialLoginFlowState(): LoginFlowState {
  return {
    step: "email",
    email: "",
    password: "",
    isLoading: false,
    loginError: "",
    rateLimitUntilMs: null,
    isLicenseBlocked: false,
    checkoutUrl: CHECKOUT_FALLBACK,
    createMatrixUrl: MATRIX_SIGNUP_URL,
    paymentLinkName: "",
    paymentLinkPriceCents: 0,
  };
}

function applyIdentify(
  prev: LoginFlowState,
  result: IdentifyResult,
): LoginFlowState {
  // Order matters: payment/Matrix gating happens before credential entry.
  // 2FA only kicks in once the user is allowed to enter credentials, so it
  // gates the password step ahead of `login`. Backend signals it via
  // IdentityStatus.requires_2fa (auth.py:177).
  let step: LoginStepKind = "login";
  if (!result.hasActivePayment) step = "need_payment";
  else if (!result.existsInMatrix) step = "need_matrix";
  else if (result.requires2fa) step = "need_2fa";

  return {
    ...prev,
    step,
    isLicenseBlocked: false,
    checkoutUrl:
      result.paymentLinkUrl || result.checkoutUrl || CHECKOUT_FALLBACK,
    createMatrixUrl: result.createMatrixUrl || MATRIX_SIGNUP_URL,
    paymentLinkName: result.paymentLinkName,
    paymentLinkPriceCents: result.paymentLinkPriceCents,
  };
}

function loginFlowStatusReducer(
  prev: LoginFlowState,
  action: LoginFlowAction,
): LoginFlowState {
  switch (action.type) {
    case "SET_ERROR":
      return {
        ...prev,
        loginError: action.error,
        rateLimitUntilMs: null,
        isLicenseBlocked: false,
      };
    case "SET_RATE_LIMIT":
      return {
        ...prev,
        loginError: action.error,
        rateLimitUntilMs: action.untilMs,
        isLoading: false,
        isLicenseBlocked: false,
      };
    case "CLEAR_RATE_LIMIT":
      return { ...prev, loginError: "", rateLimitUntilMs: null };
    case "SET_LICENSE_BLOCKED":
      return {
        ...prev,
        loginError: action.error,
        rateLimitUntilMs: null,
        isLicenseBlocked: true,
      };
    default:
      return prev;
  }
}

export function loginFlowReducer(
  prev: LoginFlowState,
  action: LoginFlowAction,
): LoginFlowState {
  switch (action.type) {
    case "SET_EMAIL":
      return { ...prev, email: action.email, isLicenseBlocked: false };
    case "SET_PASSWORD":
      return { ...prev, password: action.password };
    case "SET_LOADING":
      return { ...prev, isLoading: action.isLoading };
    case "CLEAR_LICENSE_BLOCKED":
      return { ...prev, isLicenseBlocked: false };
    case "APPLY_IDENTIFY":
      return applyIdentify(prev, action.result);
    case "APPLY_PAYMENT_LINK":
      if (prev.step !== "email") return prev;
      return {
        ...prev,
        checkoutUrl: action.url,
        paymentLinkName: action.name,
        paymentLinkPriceCents: action.priceCents,
      };
    case "BACK_TO_EMAIL":
      return {
        ...prev,
        step: "email",
        password: "",
        loginError: prev.rateLimitUntilMs === null ? "" : prev.loginError,
        isLicenseBlocked: false,
        isLoading: false,
      };
    default:
      return loginFlowStatusReducer(prev, action);
  }
}
